import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean; // Grey out option
}

interface MultiSelectDropdownProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  placeholder = 'All',
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (value: string) => {
    const option = options.find(o => o.value === value);
    if (option?.disabled) return;

    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleSelectAll = () => {
    const enabledOptions = options.filter(o => !o.disabled).map(o => o.value);
    if (selected.length === enabledOptions.length) {
      onChange([]);
    } else {
      onChange(enabledOptions);
    }
  };

  // Display text for the button
  const getDisplayText = () => {
    if (selected.length === 0) {
      return placeholder;
    }
    if (selected.length === 1) {
      const opt = options.find(o => o.value === selected[0]);
      return opt?.label || selected[0];
    }
    return `${selected.length} selected`;
  };

  const enabledOptions = options.filter(o => !o.disabled);
  const allSelected = enabledOptions.length > 0 && selected.length === enabledOptions.length;

  return (
    <div className="relative min-w-0" ref={dropdownRef}>
      {/* Dropdown Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md transition-colors min-w-0 max-w-full whitespace-nowrap ${
          selected.length > 0
            ? 'bg-blue-50 border-blue-300 text-blue-700'
            : 'bg-background-primary border-border-default text-text-primary hover:border-border-focus'
        }`}
      >
        <span className="text-xs font-medium text-text-secondary uppercase flex-shrink-0">{label}:</span>
        <span className="font-medium truncate max-w-[10rem]" title={getDisplayText()}>{getDisplayText()}</span>
        <ChevronDownIcon className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-56 max-w-[min(14rem,calc(100vw-2rem))] bg-background-elevated border border-border-default rounded-md shadow-lg z-50 max-h-64 overflow-auto">
          {/* Select All option */}
          {options.length > 1 && (
            <>
              <button
                onClick={handleSelectAll}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-surface-hover text-left"
              >
                <div
                  className={`w-4 h-4 rounded border flex items-center justify-center ${
                    allSelected
                      ? 'bg-blue-600 border-blue-600'
                      : 'border-border-default'
                  }`}
                >
                  {allSelected && <CheckIcon className="w-3 h-3 text-white" />}
                </div>
                <span className="font-medium">Select All</span>
              </button>
              <div className="border-t border-border-subtle" />
            </>
          )}

          {/* Options */}
          {options.map((option) => {
            const isSelected = selected.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => handleToggle(option.value)}
                disabled={option.disabled}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left ${
                  option.disabled
                    ? 'opacity-40 cursor-not-allowed'
                    : 'hover:bg-surface-hover'
                }`}
              >
                <div
                  className={`w-4 h-4 rounded border flex items-center justify-center ${
                    isSelected
                      ? 'bg-blue-600 border-blue-600'
                      : 'border-border-default'
                  } ${option.disabled ? 'opacity-50' : ''}`}
                >
                  {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                </div>
                <span className={option.disabled ? 'text-text-tertiary' : ''}>
                  {option.label}
                </span>
              </button>
            );
          })}

          {options.length === 0 && (
            <div className="px-3 py-4 text-sm text-text-secondary text-center">
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
}
